/**
 * The call soundboard — the joke sounds anyone in a voice channel can fire so
 * that EVERYONE in the room hears them, not just the person clicking.
 *
 * The sounds are real recordings (CC0, see public/sounds/LICENSE.txt):
 * a rimshot, a duck, a DJ airhorn, applause, crickets and a wrong-answer
 * buzzer, trimmed and loudness-normalised so they land at the same level.
 * They are fetched once, decoded with the Web Audio API and cached; the
 * first press of the board pre-warms them. Should a file fail to load (an
 * offline PWA, a blocked request), the original Web Audio synth for that
 * sound still plays, so the joke never silently misses.
 *
 * How everyone hears it: the click does NOT mix audio into the microphone
 * track (which would fight echo cancellation and drop out when you are muted).
 * It sends a tiny message on the LiveKit data channel and every client plays
 * the same sound locally — see `playSoundboard` in VoiceRoomContext.
 *
 * Everything is best-effort: audio must never break a call, so failures are
 * swallowed.
 */

let context: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!context) context = new Ctor();
  if (context.state === 'suspended') void context.resume().catch(() => undefined);
  return context;
}

/**
 * Loud enough to land as a punchline over speech, well short of painful. The
 * joke stops being funny the third time it makes someone tear their headphones
 * off, so every sound below is mixed against this ceiling.
 */
const MASTER = 0.22;

/** A buffer of white noise, the raw material for drums, applause and hiss. */
function noiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const frames = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/** One burst of filtered noise with a percussive envelope. */
function noiseHit(
  ctx: AudioContext,
  at: number,
  opts: {
    duration: number;
    type: BiquadFilterType;
    frequency: number;
    q?: number;
    gain: number;
    /** Attack in seconds; a hair longer turns a crack into a swell. */
    attack?: number;
  },
): void {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, opts.duration);
  const filter = ctx.createBiquadFilter();
  filter.type = opts.type;
  filter.frequency.setValueAtTime(opts.frequency, at);
  if (opts.q !== undefined) filter.Q.setValueAtTime(opts.q, at);
  const gain = ctx.createGain();
  const attack = opts.attack ?? 0.004;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(opts.gain, at + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + opts.duration);
  src.connect(filter).connect(gain).connect(ctx.destination);
  src.start(at);
  src.stop(at + opts.duration + 0.02);
}

/** One oscillator note, optionally gliding, optionally through a filter. */
function tone(
  ctx: AudioContext,
  at: number,
  opts: {
    type: OscillatorType;
    from: number;
    to?: number;
    duration: number;
    gain: number;
    attack?: number;
    filter?: { type: BiquadFilterType; frequency: number; q?: number };
    detune?: number;
  },
): void {
  const osc = ctx.createOscillator();
  osc.type = opts.type;
  if (opts.detune) osc.detune.setValueAtTime(opts.detune, at);
  osc.frequency.setValueAtTime(opts.from, at);
  if (opts.to !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.to), at + opts.duration);
  }
  const gain = ctx.createGain();
  const attack = opts.attack ?? 0.01;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(opts.gain, at + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + opts.duration);
  let node: AudioNode = osc;
  if (opts.filter) {
    const filter = ctx.createBiquadFilter();
    filter.type = opts.filter.type;
    filter.frequency.setValueAtTime(opts.filter.frequency, at);
    if (opts.filter.q !== undefined) filter.Q.setValueAtTime(opts.filter.q, at);
    node = node.connect(filter);
  }
  node.connect(gain).connect(ctx.destination);
  osc.start(at);
  osc.stop(at + opts.duration + 0.02);
}

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

/** The game-show "wrong answer" buzzer: low, harsh, and final. */
function bullshit(ctx: AudioContext, now: number): void {
  // Two detuned saws a fifth apart buzz far more than one; the slight detune
  // is what makes it sound like a broken klaxon rather than a synth note.
  for (const detune of [-12, 14]) {
    tone(ctx, now, {
      type: 'sawtooth',
      from: 104,
      duration: 0.62,
      gain: MASTER * 0.5,
      attack: 0.008,
      detune,
      filter: { type: 'lowpass', frequency: 1400, q: 3 },
    });
  }
  tone(ctx, now, {
    type: 'square',
    from: 52,
    duration: 0.62,
    gain: MASTER * 0.35,
    filter: { type: 'lowpass', frequency: 900 },
  });
}

/** Ba-dum-tss: two snare hits and the cymbal that lands the joke. */
function rimshot(ctx: AudioContext, now: number): void {
  const hit = (at: number, pitch: number) => {
    // A drum is a short pitched thump plus a crack of noise on top.
    tone(ctx, at, {
      type: 'sine',
      from: pitch,
      to: pitch * 0.5,
      duration: 0.16,
      gain: MASTER * 0.7,
      attack: 0.003,
    });
    noiseHit(ctx, at, {
      duration: 0.12,
      type: 'bandpass',
      frequency: 1900,
      q: 0.8,
      gain: MASTER * 0.5,
    });
  };
  hit(now, 240);
  hit(now + 0.16, 200);
  // The cymbal: bright noise with a long tail.
  noiseHit(ctx, now + 0.34, {
    duration: 0.9,
    type: 'highpass',
    frequency: 5200,
    gain: MASTER * 0.55,
  });
}

/** A duck: two quacks, because one quack sounds like a mistake. */
function quack(ctx: AudioContext, now: number): void {
  // A quack is a buzzy source swept downward through a vocal-ish band. The
  // rapid pitch drop is what the ear reads as "duck" rather than "kazoo".
  const one = (at: number, gain: number) => {
    tone(ctx, at, {
      type: 'sawtooth',
      from: 460,
      to: 210,
      duration: 0.17,
      gain,
      attack: 0.006,
      filter: { type: 'bandpass', frequency: 1100, q: 6 },
    });
    tone(ctx, at, {
      type: 'sawtooth',
      from: 230,
      to: 105,
      duration: 0.17,
      gain: gain * 0.6,
      attack: 0.006,
      filter: { type: 'bandpass', frequency: 620, q: 5 },
    });
  };
  one(now, MASTER * 0.8);
  one(now + 0.23, MASTER * 0.65);
}

/** The air horn, for when something actually lands. */
function airhorn(ctx: AudioContext, now: number): void {
  for (const [freq, detune] of [
    [233, -8],
    [311, 6],
    [466, -5],
  ] as const) {
    tone(ctx, now, {
      type: 'sawtooth',
      from: freq,
      duration: 0.85,
      gain: MASTER * 0.32,
      attack: 0.03,
      detune,
      filter: { type: 'lowpass', frequency: 2600, q: 1 },
    });
  }
}

/** A round of applause: noise that swells and fades, with hand-clap texture. */
function applause(ctx: AudioContext, now: number): void {
  noiseHit(ctx, now, {
    duration: 1.6,
    type: 'bandpass',
    frequency: 1800,
    q: 0.6,
    gain: MASTER * 0.45,
    // Slow attack: a crowd starts clapping, it doesn't detonate.
    attack: 0.25,
  });
  // Individual claps scattered over the swell so it is not just hiss.
  for (let i = 0; i < 14; i += 1) {
    noiseHit(ctx, now + 0.05 + Math.random() * 1.2, {
      duration: 0.05,
      type: 'bandpass',
      frequency: 1200 + Math.random() * 2200,
      q: 1.4,
      gain: MASTER * 0.3,
    });
  }
}

/** Crickets: the sound of nobody answering. */
function crickets(ctx: AudioContext, now: number): void {
  // Each chirp is a handful of very short high blips in quick succession.
  for (const at of [0, 0.62, 1.24]) {
    for (let i = 0; i < 4; i += 1) {
      tone(ctx, now + at + i * 0.035, {
        type: 'square',
        from: 4300,
        duration: 0.022,
        gain: MASTER * 0.18,
        attack: 0.003,
        filter: { type: 'bandpass', frequency: 4300, q: 12 },
      });
    }
  }
}

const PLAYERS = {
  bullshit,
  rimshot,
  quack,
  airhorn,
  applause,
  crickets,
} satisfies Record<string, (ctx: AudioContext, now: number) => void>;

export type SoundboardId = keyof typeof PLAYERS;

/** The board, in the order the buttons are drawn. */
export const SOUNDBOARD: Array<{ id: SoundboardId; emoji: string }> = [
  { id: 'bullshit', emoji: '💩' },
  { id: 'rimshot', emoji: '🥁' },
  { id: 'quack', emoji: '🦆' },
  { id: 'airhorn', emoji: '📣' },
  { id: 'applause', emoji: '👏' },
  { id: 'crickets', emoji: '🦗' },
];

export function isSoundboardId(value: unknown): value is SoundboardId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(PLAYERS, value);
}

/** Where the recordings live (served by the frontend as static files). */
const SAMPLE_URL = (id: SoundboardId): string => `/sounds/${id}.mp3`;

/** Recording gain — the files are normalised to -16 LUFS, so this only sets the room level. */
const SAMPLE_GAIN = 0.9;

const decoded = new Map<SoundboardId, AudioBuffer>();
const loading = new Map<SoundboardId, Promise<AudioBuffer | null>>();

/** Fetch + decode one recording, once. Resolves null (and remembers nothing) on failure. */
function loadSample(ctx: AudioContext, id: SoundboardId): Promise<AudioBuffer | null> {
  const cached = decoded.get(id);
  if (cached) return Promise.resolve(cached);
  const pending = loading.get(id);
  if (pending) return pending;
  const job = (async () => {
    try {
      const res = await fetch(SAMPLE_URL(id));
      if (!res.ok) return null;
      const buffer = await ctx.decodeAudioData(await res.arrayBuffer());
      decoded.set(id, buffer);
      return buffer;
    } catch {
      return null;
    } finally {
      loading.delete(id);
    }
  })();
  loading.set(id, job);
  return job;
}

/** Warm the cache (called when the board opens) so the first press is instant. */
export function preloadSoundboard(): void {
  const ctx = audio();
  if (!ctx) return;
  for (const { id } of SOUNDBOARD) void loadSample(ctx, id);
}

function playBuffer(ctx: AudioContext, buffer: AudioBuffer): void {
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(SAMPLE_GAIN, ctx.currentTime);
  src.connect(gain).connect(ctx.destination);
  src.start();
}

/** Play one sound here, in this browser. Never throws. */
export function playSoundboardSound(id: SoundboardId): void {
  try {
    const ctx = audio();
    if (!ctx) return;
    const ready = decoded.get(id);
    if (ready) {
      playBuffer(ctx, ready);
      return;
    }
    // Not decoded yet: fetch it (fast on a warm HTTP cache) and play; if the
    // file cannot be had, fall back to the synthesised version.
    void loadSample(ctx, id).then((buffer) => {
      try {
        if (buffer) playBuffer(ctx, buffer);
        else PLAYERS[id](ctx, ctx.currentTime + 0.02);
      } catch {
        // No audio device, a closed context, a policy refusal — the call goes on.
      }
    });
  } catch {
    // No audio device, a closed context, a policy refusal — the call goes on.
  }
}
